import type { ComponentProps, ReactNode } from "react";
import { Input } from "@elaniintc/goes-design-system";
import { Textarea } from "../ui/textarea";
import { cn } from "@/utils/theme";

interface Props extends ComponentProps<typeof Input> {
  label: string;
  required?: boolean;
  error?: string;
  limit?: number;
  count?: number;
  leadingIcon?: ReactNode;
}

const TextField = ({
  label,
  required = false,
  error,
  limit,
  count,
  className,
  leadingIcon,
  ...props
}: Props) => {
  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label
          className={cn(
            "font-semibold text-body-loud text-sm leading-2xl",
            props.disabled && "!text-on-disabled",
          )}
          htmlFor={props.id || props.name}
        >
          {label}{" "}
          {required && (
            <span
              className={cn(
                "text-sm text-danger",
                props.disabled && "!text-on-disabled",
              )}
            >
              *
            </span>
          )}
        </label>
      )}
      <div className="relative w-full">
        {leadingIcon && (
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
            {leadingIcon}
          </div>
        )}
        <Input {...props} className={cn("w-full", className)} />
      </div>
      {limit && (
        <div
          className={cn(
            "flex items-center gap-1.5 text-body-quiet",
            count === limit && "text-danger",
          )}
        >
          <span className="text-current text-xs leading-xl font-normal">
            {count ? `${count}/${limit}` : `0/${limit}`}
          </span>
        </div>
      )}
      {error && (
        <div className="text-danger flex items-center gap-1.5">
          <span className="text-current text-xs leading-xl font-normal">
            {error}
          </span>
        </div>
      )}
    </div>
  );
};

export default TextField;

interface TextAreaProps extends ComponentProps<typeof Textarea> {
  label: string;
  required?: boolean;
  error?: string;
  limit?: number;
  count?: number;
  helperText?: string;
  countAlign?: "left" | "right";
  integrated?: boolean;
}

const TextAreaField = ({
  label,
  required = false,
  error,
  limit,
  count,
  helperText,
  countAlign = "left",
  integrated = false,
  className,
  ...props
}: TextAreaProps) => {
  if (integrated) {
    return (
      <div className="flex flex-col gap-1 w-full">
        <Textarea
          {...props}
          className={cn(
            "w-full rounded-t-none border-t-0 resize-none",
            className,
          )}
        />
        {error && (
          <div className="text-danger flex items-center gap-1.5">
            <span className="text-current text-xs leading-xl font-normal">
              {error}
            </span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex justify-between w-full">
        <label
          className="font-semibold text-body-loud text-sm leading-2xl"
          htmlFor={props.id || props.name}
        >
          {label} {required && <span className="text-sm text-danger">*</span>}
        </label>
        {helperText && (
          <span className="text-sm leading-2xl text-body-quiet">
            {helperText}
          </span>
        )}
      </div>
      <Textarea {...props} />
      {limit && (
        <div
          className={cn(
            "flex items-center gap-1.5 text-xs leading-xl font-normal",
            countAlign === "left" ? "justify-start" : "justify-end",
          )}
        >
          <span
            className={cn(count === limit ? "text-danger" : "text-body-quiet")}
          >
            Caracteres:
          </span>
          <span
            className={cn(
              "text-xs leading-xl font-normal font-geist-mono",
              count === limit ? "text-danger" : "text-body ",
            )}
          >
            {count ? `${count}/${limit}` : `0/${limit}`}
          </span>
        </div>
      )}
      {error && (
        <div className="text-danger flex items-center gap-1.5">
          <span className="text-current text-xs leading-xl font-normal">
            {error}
          </span>
        </div>
      )}
    </div>
  );
};

export { TextAreaField };
